/**
 * TryItNowSection — Landing-page live verification demo.
 *
 * Two modes: carrier (FMCSA) and broker (FMCSA + bond data). Toggleable up
 * top, identifier input adapts (DOT/MC for carrier, MC for broker). The
 * "scan" UX shows a sequenced progress bar (Source · Authority · Insurance ·
 * Risk) so the wait feels like work being done, then result rows fade in
 * one at a time with a 70 ms stagger so the reveal feels fast but readable.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Shield,
  ArrowRight,
  Truck,
  Building2,
  Check,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { statusTone, BADGE_TONE_CLASS } from "@/lib/status-tone";
import { withTimeout } from "@/lib/edge";

/**
 * Pull the actual JSON error body out of a supabase-js function error.
 * Returns null when the error isn't an HTTP error or the body isn't JSON.
 * Lets us surface the real `error` code from the edge function instead of
 * the generic "Edge function returned a non-2xx status code" message.
 */
async function unwrapEdgeError(err: unknown): Promise<{
  code: string | null;
  message: string | null;
  signup_cta: boolean;
}> {
  if (err instanceof FunctionsHttpError && err.context?.response) {
    try {
      const body = await err.context.response.clone().json();
      return {
        code: typeof body?.error === "string" ? body.error : typeof body?.code === "string" ? body.code : null,
        message: typeof body?.message === "string" ? body.message : null,
        signup_cta: body?.signup_cta === true,
      };
    } catch {
      return { code: null, message: null, signup_cta: false };
    }
  }
  return { code: null, message: null, signup_cta: false };
}
import { CountryToggle, type Country } from "@/components/carrier/CountryToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { track } from "@/lib/analytics";
import { useScrollAnime } from "@/lib/anime/useScrollAnime";
import { useAnime } from "@/lib/anime/useAnime";
import { DURATION, EASE, STAGGER } from "@/lib/anime/motion-vocab";
import { ScoreExplainer } from "@/components/verify/ScoreExplainer";
import { autoParseCarrierIdentifier, isParseError } from "@/lib/carrier-identifier";
import { Sentry } from "@/lib/sentry";

// ── Types ─────────────────────────────────────────────────────────────────────

type Mode = "carrier" | "broker";

interface CarrierData {
  legal_name: string;
  dot_number: string;
  mc_number: string;
  operating_status: string;
}

interface BrokerData {
  legal_name: string;
  dba_name?: string;
  dot_number: string;
  mc_number: string;
  bond_on_file: boolean;
  insurance_on_file: boolean;
  broker_authority_status: string;
  // CA-only fields (returned when country === "CA")
  cab_id?: string;
  home_province?: string;
  trade_name?: string | null;
  customs_broker?: boolean;
  ctq_status?: string | null;
  last_verified_at?: string | null;
  verification_band?: string;
  verification_score?: number | null;
}

interface RiskAssessment {
  /** null when level === "INSUFFICIENT" — registry match without enough verified signal. */
  score: number | null;
  level: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL" | "INSUFFICIENT";
  flags: string[];
  recommendation: "PROCEED" | "CAUTION" | "BLOCK" | "REVIEW";
}

interface CarrierLookupResult {
  kind: "carrier";
  carrier: CarrierData;
  risk_assessment: RiskAssessment;
}

interface BrokerLookupResult {
  kind: "broker";
  country?: "US" | "CA";
  broker: BrokerData;
  risk_assessment: RiskAssessment;
  additional_matches?: Array<{
    cab_id: string;
    legal_name: string;
    home_province: string;
    verification_band: string | null;
    verification_score: number | null;
  }>;
}

type LookupResult = CarrierLookupResult | BrokerLookupResult;

// ── Helpers ───────────────────────────────────────────────────────────────────

const FLAG_LABELS: Record<string, string> = {
  recently_registered: "Recently registered (< 90 days)",
  no_insurance: "No insurance on file",
  out_of_service: "Out of service",
  not_authorized: "Not authorized to operate",
  safety_concern: "Safety rating concern",
  small_fleet: "Small fleet (< 3 units)",
  no_surety_bond: "No surety bond on file",
  no_broker_authority: "No active broker authority",
  authority_too_new: "Authority less than 90 days old",
  revoked_authority: "Broker authority revoked",
  multiple_flags: "Multiple risk indicators present",
};

function recConfig(rec: string) {
  switch (rec) {
    case "PROCEED":
      return {
        label: "PROCEED",
        colorClass: "text-emerald-400",
        borderClass: "border-emerald-500/30",
        bgClass: "bg-emerald-500/10",
        Icon: ShieldCheck,
      };
    case "CAUTION":
      return {
        label: "CAUTION",
        colorClass: "text-amber-400",
        borderClass: "border-amber-500/30",
        bgClass: "bg-amber-500/10",
        Icon: AlertTriangle,
      };
    case "BLOCK":
      return {
        label: "BLOCK",
        colorClass: "text-red-400",
        borderClass: "border-red-500/30",
        bgClass: "bg-red-500/10",
        Icon: ShieldAlert,
      };
    case "REVIEW":
      return {
        label: "REVIEW",
        colorClass: "text-muted-foreground",
        borderClass: "border-border",
        bgClass: "bg-muted/10",
        Icon: Shield,
      };
    default:
      return {
        label: "UNKNOWN",
        colorClass: "text-muted-foreground",
        borderClass: "border-border",
        bgClass: "bg-muted/10",
        Icon: Shield,
      };
  }
}

function riskLevelColor(level: string) {
  switch (level) {
    case "LOW":
      return "text-emerald-400";
    case "MEDIUM":
      return "text-amber-400";
    case "HIGH":
      return "text-orange-400";
    case "CRITICAL":
      return "text-red-400";
    default:
      return "text-muted-foreground";
  }
}

// ── Scanning animation ────────────────────────────────────────────────────────
//
// Four sequential "stages" the user watches tick off while the network call
// is in flight. Each stage has a fixed minimum dwell so even on a fast
// response, the user perceives substantive work happening (the actual lookup
// can take 0.3–2.0 s; the animation runs in parallel and we wait for the
// later of the two before revealing the result).

const CARRIER_STAGES = [
  "Querying FMCSA",
  "Checking authority",
  "Pulling insurance",
  "Scoring risk",
];

const BROKER_STAGES = [
  "Querying FMCSA",
  "Checking broker authority",
  "Verifying surety bond",
  "Scoring risk",
];

const STAGE_MS = 320; // dwell per stage; total ≈ 1.3s minimum scan time

function ScanProgress({ mode }: { mode: Mode }) {
  const stages = mode === "broker" ? BROKER_STAGES : CARRIER_STAGES;
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step >= stages.length) return;
    const t = window.setTimeout(() => setStep((s) => s + 1), STAGE_MS);
    return () => window.clearTimeout(t);
  }, [step, stages.length]);

  const pct = Math.min(100, (step / stages.length) * 100);

  return (
    <div
      className="glass-card-strong rounded-2xl p-6 sm:p-8 mb-3 relative overflow-hidden verify-halo"
      role="status"
      aria-live="polite"
      aria-label="Scanning"
    >
      {/* Decorative scan layers · radar grid wash + vertical beam sweep.
          Pure CSS keyframes, paused for prefers-reduced-motion. */}
      <div className="verify-grid" aria-hidden="true" />
      <div className="verify-beam" aria-hidden="true" />

      {/* Orbiting "data packets" representing the live data sources being
          queried · FMCSA, AUTH, INS, BOND, RISK. They circle the card
          while the scan runs. */}
      <div
        className="absolute left-1/2 top-1/2 pointer-events-none"
        style={{ transform: "translate(-50%, -50%)" }}
        aria-hidden="true"
      >
        {(mode === "broker"
          ? ["FMCSA", "AUTH", "BOND", "RISK"]
          : ["FMCSA", "AUTH", "INS", "RISK"]
        ).map((label, i, arr) => {
          const r = 130 + (i % 2) * 18;
          const dur = 3.4 + i * 0.25;
          const delay = -(i * dur) / arr.length; // evenly spaced around orbit
          return (
            <span
              key={label}
              className="verify-packet"
              style={
                {
                  ["--orbit-r" as string]: `${r}px`,
                  ["--orbit-d" as string]: `${dur}s`,
                  ["--orbit-delay" as string]: `${delay}s`,
                } as React.CSSProperties
              }
            >
              {label}
            </span>
          );
        })}
      </div>

      {/* CSS-driven progress track · no looping anime.js instance needed */}
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes scan-shimmer {
            0%   { transform: translateX(-30%); }
            100% { transform: translateX(130%); }
          }
          .scan-shimmer {
            animation: scan-shimmer 1.1s ease-in-out infinite;
          }
          @keyframes scan-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: 0.4; transform: scale(0.7); }
          }
          .scan-pulse {
            animation: scan-pulse 1.1s ease-in-out infinite;
          }
        }
      `}</style>
      <div className="relative z-[1] h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06] mb-5">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary shadow-[0_0_12px_hsl(160_100%_45%/0.6)] transition-[width] duration-300 ease-out"
          style={{ width: `${Math.max(8, pct)}%` }}
        />
        <div className="scan-shimmer absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      </div>

      {/* Stage list · sits above the scan layers via z-index */}
      <ul className="relative z-[1] space-y-2">
        {stages.map((label, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <li
              key={label}
              className="flex items-center gap-3 text-sm"
              style={{
                opacity: 1,
                transform: "none",
                transition: `opacity 0.25s ease ${i * 0.05}s, transform 0.25s ease ${i * 0.05}s`,
              }}
            >
              <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
                  done
                    ? "bg-primary border-primary text-primary-foreground"
                    : active
                      ? "border-primary text-primary"
                      : "border-white/10 text-muted-foreground"
                }`}
              >
                {done ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : active ? (
                  <span className="scan-pulse h-1.5 w-1.5 rounded-full bg-primary" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current opacity-40" />
                )}
              </span>
              <span
                className={`font-mono text-xs uppercase tracking-[0.15em] ${
                  done
                    ? "text-foreground"
                    : active
                      ? "text-primary"
                      : "text-muted-foreground/60"
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TryItNowSection() {
  const [mode, setMode] = useState<Mode>("carrier");
  const [country, setCountry] = useState<Country>("US");
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signupPrompt, setSignupPrompt] = useState<{ kind: "carrier" | "broker"; reason: string } | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // When set, the next identifier-change does NOT trigger the autocomplete
  // dropdown. Reset by typing again. Used by chip + dropdown clicks to
  // suppress the cascade ("user already chose, don't second-guess them").
  const suppressAutocompleteRef = useRef(false);

  /** Set the identifier and immediately submit using the override path so
      we don't race React's state batching (handleSubmit reads `identifier`
      from closure; setting state then submitting on rAF would still see
      the stale value). */
  const setAndSubmit = (next: string) => {
    suppressAutocompleteRef.current = true;
    setCaBrokerOpen(false);
    setCaBrokerHits([]);
    setIdentifier(next);
    handleSubmit(null, next);
  };

  // CA broker name autocomplete · matches the dashboard form's UX so landing
  // visitors can pick a real broker from the registry instead of guessing
  // names. Only active in mode="broker" + country="CA".
  interface CaBrokerHit {
    cab_id: string;
    legal_name: string;
    trade_name: string | null;
    home_province: string | null;
    verification_band: "verified" | "reviewed" | "unverified" | null;
  }
  const [caBrokerHits, setCaBrokerHits] = useState<CaBrokerHit[]>([]);
  const [caBrokerSearching, setCaBrokerSearching] = useState(false);
  const [caBrokerOpen, setCaBrokerOpen] = useState(false);
  const caBrokerEnabled = mode === "broker" && country === "CA";
  useEffect(() => {
    if (!caBrokerEnabled) {
      setCaBrokerHits([]);
      setCaBrokerOpen(false);
      return;
    }
    // Respect the suppress flag set by chip / dropdown clicks. The user
    // already chose a value; opening the dropdown again would compete with
    // the result reveal that's about to fire.
    if (suppressAutocompleteRef.current) {
      suppressAutocompleteRef.current = false;
      return;
    }
    const q = identifier.trim();
    if (q.length < 3) {
      setCaBrokerHits([]);
      setCaBrokerSearching(false);
      return;
    }
    setCaBrokerSearching(true);
    const handle = window.setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc("search_ca_brokers_public", {
          p_query: q,
          p_limit: 5,
        });
        if (error) {
          setCaBrokerHits([]);
        } else {
          setCaBrokerHits((data ?? []) as CaBrokerHit[]);
          setCaBrokerOpen(true);
        }
      } finally {
        setCaBrokerSearching(false);
      }
    }, 280);
    return () => window.clearTimeout(handle);
  }, [identifier, caBrokerEnabled]);

  // ── Scroll-triggered header reveals ────────────────────────────────────────

  const eyebrowRef = useScrollAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [16, 0],
    duration: DURATION.base,
    ease: EASE.soft,
    threshold: 0.2,
    once: true,
  });

  const headlineRef = useScrollAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [20, 0],
    duration: DURATION.base,
    ease: EASE.soft,
    threshold: 0.2,
    once: true,
    delay: STAGGER.base,
  });

  const subheadRef = useScrollAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [16, 0],
    duration: DURATION.base,
    ease: EASE.soft,
    threshold: 0.2,
    once: true,
    delay: STAGGER.base * 2,
  });

  const toggleRef = useScrollAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [12, 0],
    duration: DURATION.base,
    ease: EASE.soft,
    threshold: 0.2,
    once: true,
    delay: STAGGER.base * 3,
  });

  const inputAreaRef = useScrollAnime<HTMLDivElement>({
    opacity: [0, 1],
    scale: [0.98, 1],
    duration: DURATION.base,
    ease: EASE.soft,
    threshold: 0.2,
    once: true,
    delay: STAGGER.base * 4,
  });

  const chipsRef = useScrollAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [8, 0],
    duration: DURATION.fast,
    ease: EASE.soft,
    threshold: 0.2,
    once: true,
    delay: STAGGER.base * 5,
  });

  // ── Result card pop-in (async, triggered from success branch) ───────────────

  const { ref: resultCardRef, play: playResult } = useAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [16, 0],
    scale: [0.97, 1],
    duration: DURATION.base,
    ease: EASE.snap,
    autoPlay: false,
  });

  // ── Error card pop-in ───────────────────────────────────────────────────────

  const { ref: errorCardRef, play: playError } = useAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [8, 0],
    duration: DURATION.fast,
    ease: EASE.soft,
    autoPlay: false,
  });

  // Trigger the error animation whenever error state is set
  useEffect(() => {
    if (error) {
      // Small rAF delay so the node is guaranteed to be in the DOM
      const id = requestAnimationFrame(() => playError());
      return () => cancelAnimationFrame(id);
    }
  }, [error, playError]);

  // ── Form logic ──────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent | null, override?: string) => {
    e?.preventDefault();
    // CA identifiers carry letters and dashes the digits-only strip would
    // destroy. Different shapes per branch:
    //   CA broker  → legal name string (e.g. "Day & Ross"). Preserve case.
    //   CA carrier → NSC like "MB9002108", CVOR digits, or CTQ "R-12345".
    //                Uppercase + dash-preserve so the registry match works.
    //   US (any)   → MC / DOT identifier; preserve the prefix so the edge
    //                function's classifier knows whether bare digits should
    //                resolve as MC or DOT.
    const raw = override ?? identifier;
    let trimmed: string;
    if (country === "CA" && mode === "broker") {
      trimmed = raw.trim().replace(/\s+/g, " ");
    } else if (country === "CA") {
      trimmed = raw.trim().toUpperCase().replace(/\s+/g, "");
    } else {
      trimmed = raw.trim().toUpperCase().replace(/\s+/g, "");
    }
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setSignupPrompt(null);
    setResult(null);

    // Broadcast loading state so peripheral UI (e.g. the hero preview card)
    // can show a skeleton placeholder while the first live query runs.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("try-it-now:loading", { detail: { mode } }));
    }

    // Run the lookup and a minimum animation duration in parallel · whichever
    // is slower wins. Keeps the reveal from snapping in the moment the
    // edge function returns; user sees the scan complete first.
    const minScan = new Promise<void>((res) =>
      window.setTimeout(res, STAGE_MS * 4 + 150),
    );

    try {
      if (mode === "broker") {
        const isBrokerCA = country === "CA";
        const requestBody = isBrokerCA
          ? { identifier: trimmed, country: "CA" }
          : { mc_number: trimmed };
        const lookupPromise = withTimeout(
          supabase.functions.invoke("broker-lookup-public", { body: requestBody }),
          35_000,
          isBrokerCA
            ? "Canadian broker registry is slow to respond. Try again."
            : "Broker verification service is slow to respond. Try again.",
        );

        const [{ data, error: fnError }] = await Promise.all([
          lookupPromise,
          minScan,
        ]);

        if (fnError) {
          await handleEdgeError(fnError);
        } else if (data?.broker) {
          setResult({
            kind: "broker",
            country: data.country === "CA" ? "CA" : "US",
            broker: data.broker as BrokerData,
            risk_assessment: data.risk_assessment as RiskAssessment,
            additional_matches: data.additional_matches,
          });
          track("try_it_now_submitted", { mode, country, has_result: true });
          setTimeout(() => {
            resultRef.current?.focus();
            playResult();
          }, 50);
        } else {
          setError(
            isBrokerCA
              ? "No Canadian broker found matching that name or CAB-ID. Try a different spelling."
              : "No broker found with that MC number. Double-check and try again.",
          );
          track("try_it_now_submitted", { mode, country, has_result: false });
        }
      } else if (country === "CA") {
        // CA carriers go through carrier-lookup-ca, which supports anonymous
        // calls (with a 10/hr per-IP rate limit) and expects a structured
        // body with explicit cvor_number / ctq_number / nsc_number / dot_number
        // plus a province. Parse the free-text input first so we can hand
        // the right shape over.
        const parsed = autoParseCarrierIdentifier(trimmed);
        if (isParseError(parsed) || parsed.jurisdiction !== "CA") {
          setError(
            isParseError(parsed)
              ? `${parsed.error} ${parsed.hint}`
              : "Use a Canadian identifier: CVOR, CTQ, NSC (with province prefix like MB9002108), or cross-border DOT.",
          );
          await minScan;
          track("try_it_now_submitted", { mode, country, has_result: false });
          return;
        }
        const lookupPromise = withTimeout(
          supabase.functions.invoke("carrier-lookup-ca", { body: parsed.body }),
          35_000,
          "Provincial registry is slow to respond. Try again.",
        );

        const [{ data, error: fnError }] = await Promise.all([
          lookupPromise,
          minScan,
        ]);

        if (fnError) {
          await handleEdgeError(fnError);
        } else if (data?.carrier) {
          // carrier-lookup-ca shape includes province + verification_source
          // fields the landing demo's CarrierData type doesn't model. Map
          // down to the common subset; the dashboard tools handle the full
          // shape elsewhere.
          const c = data.carrier as Record<string, unknown>;
          setResult({
            kind: "carrier",
            carrier: {
              legal_name: String(c.legal_name ?? ""),
              dot_number: String(c.dot_number ?? ""),
              mc_number: String(c.mc_number ?? c.nsc_number ?? c.cvor_number ?? c.ctq_number ?? ""),
              operating_status: String(c.certificate_status ?? c.operating_status ?? "UNKNOWN"),
            },
            risk_assessment: data.risk_assessment as RiskAssessment,
          });
          track("try_it_now_submitted", { mode, country, has_result: true });
          setTimeout(() => {
            resultRef.current?.focus();
            playResult();
          }, 50);
        } else {
          setError("No Canadian carrier found. Check the number and try again.");
          track("try_it_now_submitted", { mode, country, has_result: false });
        }
      } else {
        const lookupPromise = withTimeout(
          supabase.functions.invoke("carrier-lookup-public", { body: { identifier: trimmed, country } }),
          35_000,
          "FMCSA is slow to respond. Try again.",
        );

        const [{ data, error: fnError }] = await Promise.all([
          lookupPromise,
          minScan,
        ]);

        if (fnError) {
          await handleEdgeError(fnError);
        } else if (data?.carrier) {
          setResult({
            kind: "carrier",
            carrier: data.carrier as CarrierData,
            risk_assessment: data.risk_assessment as RiskAssessment,
          });
          track("try_it_now_submitted", { mode, country, has_result: true });
          setTimeout(() => {
            resultRef.current?.focus();
            playResult();
          }, 50);
        } else {
          setError("No carrier found. Check the number and try again.");
          track("try_it_now_submitted", { mode, country, has_result: false });
        }
      }
    } catch (err: unknown) {
      // Surface unexpected failures to Sentry — without this the catch
      // swallowed every network/parse error and the user just saw
      // "Connection error" while the dashboard had no signal at all.
      Sentry.captureException(err, {
        tags: { surface: "try_it_now", mode, country },
      });
      setError("Connection error. Please check your internet and try again.");
    } finally {
      setLoading(false);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("try-it-now:settled"));
      }
    }
  };

  const handleEdgeError = async (err: unknown) => {
    // Try to read the JSON error body so we can route on the actual code
    // (signup_required, ca_lookup_failed, not_found, etc.) instead of
    // string-matching the generic supabase-js message.
    const { code, message: detail, signup_cta } = await unwrapEdgeError(err);

    if (code === "signup_required" || signup_cta) {
      setSignupPrompt({
        kind: mode,
        reason:
          mode === "carrier" && country === "CA"
            ? "Anonymous demo covers FMCSA only. Sign up free to verify CVOR, CTQ, and NSC carriers."
            : detail ?? "This lookup requires a free account. Sign up to continue.",
      });
      track("try_it_now_signup_prompted", { mode, country });
      return;
    }

    if (code === "not_found") {
      setError(
        mode === "broker"
          ? "No broker found matching that input. Double-check and try again."
          : "No carrier found with that number. Double-check and try again.",
      );
      return;
    }

    if (code === "ca_lookup_failed") {
      setError("The Canadian broker registry is temporarily unavailable. Try again in a moment.");
      return;
    }

    if (code === "query_too_short") {
      setError("Type at least 3 characters of the broker's name (or paste a full CAB ID).");
      return;
    }

    // Fallback to legacy string-match for transports that didn't go through
    // the unwrap (timeouts, network errors, etc.).
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("404") || msg.includes("not_found")) {
      setError(
        mode === "broker"
          ? "No broker found matching that input. Double-check and try again."
          : "No carrier found with that number. Double-check and try again.",
      );
    } else if (msg.includes("502") || msg.includes("unavailable")) {
      // Use CA-neutral copy when in broker CA mode or CA carrier mode — FMCSA
      // is not involved in those paths (CRA / provincial registry).
      const isCAPath = (mode === "broker" && country === "CA") || (mode === "carrier" && country === "CA");
      setError(
        isCAPath
          ? "Couldn't reach the Canadian registry. Try again in a moment."
          : "FMCSA is temporarily unavailable. Try again in a few minutes.",
      );
    } else {
      setError(detail ?? "Something went wrong. Please try again.");
    }
  };

  const handleCountryChange = (c: Country) => {
    setCountry(c);
    setIdentifier("");
    setResult(null);
    setError(null);
    setSignupPrompt(null);
    // Clear the CA broker autocomplete state so a stale dropdown can't
    // flash on top of the new mode/country.
    setCaBrokerHits([]);
    setCaBrokerOpen(false);
  };

  const handleModeChange = (m: Mode) => {
    if (m === mode) return;
    setMode(m);
    setIdentifier("");
    setResult(null);
    setError(null);
    setSignupPrompt(null);
    setCaBrokerHits([]);
    setCaBrokerOpen(false);
    // Brokers: US-only data source; force back to US toggle.
    if (m === "broker") setCountry("US");
  };

  const rec = result ? recConfig(result.risk_assessment.recommendation) : null;
  const subjectName =
    result?.kind === "carrier"
      ? result.carrier.legal_name
      : result?.kind === "broker"
        ? result.broker.legal_name
        : "";

  return (
    <section id="try-it-now" data-section="try-it-now" className="landing-section">
      <div className="landing-section-inner">
        <div className="max-w-3xl mx-auto">
          {/* Label */}
          <div ref={eyebrowRef} style={{ opacity: 0 }} className="text-center mb-4">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
              Try it now &middot; no signup
            </span>
          </div>

          {/* Heading */}
          <div ref={headlineRef} style={{ opacity: 0 }} className="text-center mb-4">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-foreground">
              Verify any carrier or broker in 2 seconds
            </h2>
          </div>

          {/* Sub */}
          <div ref={subheadRef} style={{ opacity: 0 }} className="text-center mb-10">
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
              Paste a DOT or MC number. See operating status, surety bond, insurance,
              and risk assessment pulled live from FMCSA. No card, no account.
            </p>
          </div>

          {/* Mode toggle · carrier / broker */}
          <div ref={toggleRef} style={{ opacity: 0 }}>
            <div className="flex justify-center mb-5">
              <div
                role="tablist"
                aria-label="Lookup type"
                className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.02] p-1"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "carrier"}
                  onClick={() => handleModeChange("carrier")}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                    mode === "carrier"
                      ? "bg-primary text-primary-foreground shadow-[0_0_20px_hsl(160_100%_45%/0.3)]"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Truck className="h-4 w-4" />
                  Carrier
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "broker"}
                  onClick={() => handleModeChange("broker")}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                    mode === "broker"
                      ? "bg-primary text-primary-foreground shadow-[0_0_20px_hsl(160_100%_45%/0.3)]"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Building2 className="h-4 w-4" />
                  Broker
                </button>
              </div>
            </div>
          </div>

          {/* Input form OR scanning animation */}
          <div ref={inputAreaRef} style={{ opacity: 0 }}>
            {loading ? (
              <ScanProgress mode={mode} />
            ) : (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="glass-card-strong rounded-2xl p-6 sm:p-8 mb-3"
              >
                {/* Country toggle · available in both modes. US broker = FMCSA
                    bond + authority data; CA broker = Cipher & Row's curated
                    Canadian broker registry (NTBA / ODBus / CBSA / CIFFA). */}
                <div className="mb-5">
                  <CountryToggle value={country} onChange={handleCountryChange} />
                </div>

                {/* Input row */}
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  <div className="flex-1 relative">
                    <Input
                      type="text"
                      inputMode={mode === "broker" && country === "CA" ? "text" : country === "CA" ? "text" : "numeric"}
                      aria-label={
                        mode === "broker"
                          ? country === "CA"
                            ? "Canadian broker name or CAB-ID"
                            : "MC number"
                          : country === "CA"
                            ? "CVOR or NSC number"
                            : "DOT or MC number"
                      }
                      placeholder={
                        mode === "broker"
                          ? country === "CA"
                            ? "Company name, e.g. Freight In Motion"
                            : "e.g. MC-131029"
                          : country === "CA"
                            ? "e.g. NSC-MB9002108 or CVOR-123456789"
                            : "e.g. DOT-53467 or MC-131029"
                      }
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      onFocus={() => caBrokerEnabled && caBrokerHits.length > 0 && setCaBrokerOpen(true)}
                      onBlur={() => window.setTimeout(() => setCaBrokerOpen(false), 150)}
                      className="bg-white/[0.03] border-white/[0.06] focus:border-primary/40 h-12 text-base w-full"
                      disabled={loading}
                      autoComplete="off"
                      aria-autocomplete={caBrokerEnabled ? "list" : undefined}
                      aria-expanded={caBrokerEnabled ? caBrokerOpen : undefined}
                    />
                    {/* CA broker autocomplete dropdown · only renders in
                        mode=broker + country=CA when we have hits. */}
                    {caBrokerEnabled && caBrokerOpen && caBrokerHits.length > 0 && (
                      <ul
                        role="listbox"
                        className="absolute z-30 left-0 right-0 top-full mt-1 max-h-[280px] overflow-y-auto rounded-xl border border-white/[0.08] bg-[hsl(240_8%_7%/0.98)] backdrop-blur-md shadow-2xl"
                      >
                        {caBrokerHits.map((hit) => (
                          <li key={hit.cab_id}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                // mousedown beats input blur — without this the dropdown closes before click registers.
                                e.preventDefault();
                                // setAndSubmit fires the lookup with the picked
                                // legal_name immediately so the user sees the
                                // result without a second click.
                                setAndSubmit(hit.legal_name);
                              }}
                              className="w-full text-left px-4 py-2.5 hover:bg-white/[0.04] flex items-start gap-3 transition-colors border-b border-white/[0.04] last:border-b-0"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-[13px] font-semibold text-foreground truncate">
                                    {hit.legal_name}
                                  </span>
                                  {hit.verification_band && (
                                    <span
                                      className={`text-[8.5px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded border ${
                                        hit.verification_band === "verified"
                                          ? "bg-primary/15 text-primary border-primary/40"
                                          : hit.verification_band === "reviewed"
                                            ? "bg-amber-400/15 text-amber-400 border-amber-400/40"
                                            : "bg-white/[0.04] text-muted-foreground border-white/[0.08]"
                                      }`}
                                    >
                                      {hit.verification_band}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                  <span className="font-mono">{hit.cab_id}</span>
                                  {hit.home_province && (
                                    <>
                                      <span aria-hidden>·</span>
                                      <span>{hit.home_province}</span>
                                    </>
                                  )}
                                  {hit.trade_name && hit.trade_name !== hit.legal_name && (
                                    <>
                                      <span aria-hidden>·</span>
                                      <span className="italic truncate">DBA {hit.trade_name}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {caBrokerEnabled && caBrokerSearching && identifier.trim().length >= 3 && !caBrokerOpen && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground/60">
                        Searching…
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    variant="mint"
                    disabled={loading || !identifier.trim()}
                    className="h-12 px-8 font-bold shadow-[0_0_20px_hsl(160_100%_45%/0.2)] shrink-0"
                  >
                    Verify
                  </Button>
                </div>
              </form>
            )}

            {/* Hint chips · click to auto-populate. All numbers are confirmed-live
                FMCSA/registry hits as of 2026-05-02 (vernot/test/fixtures/real-carriers.ts).
                Rendered unconditionally so they survive the scan/result remount cycle —
                useScrollAnime fires once on first scroll, so toggling the chips off during
                loading would leave them invisible on the next render. */}
            <div ref={chipsRef} style={{ opacity: 0 }}>
              <div
                className={`flex flex-wrap items-center justify-center gap-2 text-xs mb-8 transition-opacity ${
                  loading ? "opacity-50 pointer-events-none" : ""
                } ${
                  /* Hide the chip row when the autocomplete dropdown is
                     open, otherwise the dropdown overlay sits ON TOP of
                     the chips and makes the surface look glitchy. */
                  caBrokerEnabled && caBrokerOpen && caBrokerHits.length > 0
                    ? "opacity-0 pointer-events-none"
                    : ""
                }`}
              >
                <span className="text-muted-foreground/60">Try a real one:</span>
                {mode === "broker" ? (
                  country === "CA" ? (
                    <>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => setAndSubmit("Day & Ross")}
                        title="Day & Ross · major Canadian broker/carrier"
                        className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                      >
                        Day &amp; Ross
                      </button>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => setAndSubmit("Polaris Transport")}
                        title="Polaris Transport · ON-based broker/carrier"
                        className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                      >
                        Polaris Transport
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => setAndSubmit("MC-131029")}
                        title="C.H. Robinson · MC 131029 · largest US broker"
                        className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                      >
                        MC-131029
                      </button>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => setAndSubmit("MC-322572")}
                        title="Total Quality Logistics · MC 322572"
                        className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                      >
                        MC-322572
                      </button>
                    </>
                  )
                ) : country === "CA" ? (
                  <>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => setAndSubmit("NSC-MB9002108")}
                      title="Bison Transport · MB NSC · large national carrier"
                      className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                    >
                      NSC-MB9002108
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => setAndSubmit("NSC-MB9014695")}
                      title="Kleysen Group · MB NSC · cross-border"
                      className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                    >
                      NSC-MB9014695
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => setAndSubmit("DOT-53467")}
                      title="Werner Enterprises · DOT 53467 · ACTIVE, 9863 power units"
                      className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                    >
                      DOT-53467
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => setAndSubmit("DOT-80806")}
                      title="J.B. Hunt · DOT 80806 · largest US truckload"
                      className="px-2.5 py-1 rounded-full border border-border/60 bg-muted/40 font-mono text-primary/90 hover:border-primary/40 hover:bg-primary/10 transition"
                    >
                      DOT-80806
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Signup-required prompt · friendlier than a generic error when the
              edge function gates a path behind a free account (e.g. CA carrier
              lookups). Shows what's coverable now vs after signup, plus a CTA. */}
          {signupPrompt && (
            <div
              className="glass-card-strong rounded-2xl p-5 mb-6 border border-primary/30 bg-primary/[0.03]"
              role="status"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-primary mb-1">
                    Free account needed
                  </p>
                  <p className="text-sm text-foreground leading-relaxed">
                    {signupPrompt.reason}
                  </p>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/[0.05]">
                    <Link
                      to="/signup"
                      onClick={() =>
                        track("try_it_now_signup_clicked", { mode, country })
                      }
                      className="inline-flex items-center gap-1 text-[12px] text-primary font-semibold hover:translate-x-0.5 transition-transform"
                    >
                      Sign up free <ArrowRight className="h-3 w-3" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setSignupPrompt(null);
                        if (mode === "carrier" && country === "CA") {
                          // Suggest the user try a US identifier instead.
                          setIdentifier("");
                          handleCountryChange("US");
                        }
                      }}
                      className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {mode === "carrier" && country === "CA" ? "Try a US identifier" : "Dismiss"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && !signupPrompt && (
            <div
              ref={errorCardRef}
              style={{ opacity: 0 }}
              className="glass-card rounded-2xl p-5 mb-6 border border-destructive/20"
              role="alert"
            >
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Result card · rows fade in one at a time after scan completes */}
          {result && rec && (
            <div
              ref={(node) => {
                // Assign both resultRef (for focus) and resultCardRef (for anime)
                (resultRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
                (resultCardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
              }}
              style={{ opacity: 0 }}
              aria-live="polite"
              aria-atomic="true"
              tabIndex={-1}
              className="outline-none relative"
            >
              {/* One-shot shockwave when result lands · pure CSS, fades
                  itself out via animation-fill-mode: forwards. */}
              <div className="verify-shockwave" aria-hidden="true" />
              <div className="glass-card-strong rounded-2xl overflow-hidden relative">
                {/* Top split */}
                <div className="flex flex-col sm:flex-row">
                  {/* Left: subject identity */}
                  <div className="flex-1 p-6 border-b sm:border-b-0 sm:border-r border-white/[0.06]">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mb-2">
                      {result.kind === "broker" ? "Broker" : "Carrier"}
                    </p>
                    <p className="text-lg font-bold text-foreground mb-3 leading-snug">
                      {subjectName || "Unknown"}
                    </p>

                    <div className="space-y-2 text-sm">
                      {result.kind === "broker" && result.country === "CA" ? (
                        <>
                          {result.broker.trade_name &&
                            result.broker.trade_name !== result.broker.legal_name && (
                              <div className="flex items-center gap-2 -mt-1 mb-1">
                                <span className="text-muted-foreground w-16 shrink-0">DBA</span>
                                <span className="italic text-foreground/90 truncate">
                                  {result.broker.trade_name}
                                </span>
                              </div>
                            )}
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">CAB ID</span>
                            <span className="font-mono font-semibold text-foreground">
                              {result.broker.cab_id || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">Province</span>
                            <span className="font-semibold text-foreground">
                              {result.broker.home_province || "-"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">Status</span>
                            <Badge
                              className={
                                result.broker.verification_band === "verified"
                                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                  : result.broker.verification_band === "reviewed"
                                    ? "bg-blue-500/15 text-blue-400 border-blue-500/30"
                                    : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                              }
                            >
                              {(result.broker.verification_band || "unverified").toUpperCase()}
                            </Badge>
                          </div>
                          {result.broker.last_verified_at && (
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground w-16 shrink-0">Verified</span>
                              <span className="text-foreground/80 text-xs">
                                {(() => {
                                  const d = new Date(result.broker.last_verified_at);
                                  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
                                  if (days === 0) return "today";
                                  if (days === 1) return "yesterday";
                                  if (days < 30) return `${days} days ago`;
                                  if (days < 365) return `${Math.floor(days / 30)} months ago`;
                                  return d.toLocaleDateString();
                                })()}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 flex-wrap pt-1">
                            {result.broker.customs_broker && (
                              <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/5">
                                CBSA customs broker
                              </Badge>
                            )}
                            {result.broker.ctq_status === "active" && (
                              <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/5">
                                CTQ active
                              </Badge>
                            )}
                            {result.broker.ctq_status === "blocked" && (
                              <Badge variant="outline" className="border-red-500/30 text-red-400 bg-red-500/5">
                                CTQ blocked
                              </Badge>
                            )}
                          </div>
                          {result.additional_matches && result.additional_matches.length > 0 && (
                            <div className="pt-3 mt-1 border-t border-white/[0.06]">
                              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.18em] mb-2">
                                Other matches
                              </p>
                              <ul className="flex flex-col gap-1">
                                {result.additional_matches.slice(0, 3).map((m) => (
                                  <li key={m.cab_id} className="flex items-center gap-2 text-xs">
                                    <span className="font-mono text-muted-foreground shrink-0">{m.cab_id}</span>
                                    <span className="text-foreground/80 truncate">{m.legal_name}</span>
                                    <span className="text-muted-foreground/60 shrink-0">· {m.home_province}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">DOT</span>
                            <span className="font-mono font-semibold text-foreground">
                              {(result.kind === "carrier"
                                ? result.carrier.dot_number
                                : result.broker.dot_number) || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">MC</span>
                            <span className="font-mono font-semibold text-foreground">
                              {(result.kind === "carrier"
                                ? result.carrier.mc_number
                                : result.broker.mc_number) || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-16 shrink-0">
                              {result.kind === "broker" ? "Authority" : "Status"}
                            </span>
                            {result.kind === "carrier" ? (
                              <Badge className={BADGE_TONE_CLASS[statusTone(result.carrier.operating_status)]}>
                                {result.carrier.operating_status}
                              </Badge>
                            ) : (
                              <Badge className={BADGE_TONE_CLASS[statusTone(result.broker.broker_authority_status)]}>
                                {result.broker.broker_authority_status || "UNKNOWN"}
                              </Badge>
                            )}
                          </div>

                          {/* Broker-only: bond + insurance pills (US) */}
                          {result.kind === "broker" && (
                            <div className="flex items-center gap-2 flex-wrap pt-1">
                              <Badge
                                variant="outline"
                                className={
                                  result.broker.bond_on_file
                                    ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/5"
                                    : "border-red-500/30 text-red-400 bg-red-500/5"
                                }
                              >
                                {result.broker.bond_on_file
                                  ? "Surety bond on file"
                                  : "No surety bond"}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={
                                  result.broker.insurance_on_file
                                    ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/5"
                                    : "border-amber-500/30 text-amber-400 bg-amber-500/5"
                                }
                              >
                                {result.broker.insurance_on_file
                                  ? "Insurance on file"
                                  : "No BIPD insurance"}
                              </Badge>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right: risk assessment · "Insufficient" branch shows
                      "Not enough data" instead of pretending to score 0/100. */}
                  <div
                    className={`sm:w-56 p-6 flex flex-col items-start sm:items-center justify-center gap-3 ${rec.bgClass}`}
                  >
                    <div className="flex items-center gap-2">
                      <rec.Icon className={`h-6 w-6 ${rec.colorClass}`} />
                      <span className={`text-2xl font-black tracking-tight ${rec.colorClass}`}>
                        {rec.label}
                      </span>
                    </div>
                    {result.risk_assessment.level === "INSUFFICIENT" ? (
                      <div className="text-center">
                        <p className="text-[18px] font-bold text-foreground leading-tight">
                          Not enough data
                          <br />to score
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
                          Sign up to run a full verification.
                        </p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className={`text-4xl font-black font-mono ${rec.colorClass}`}>
                          {result.risk_assessment.score ?? "–"}
                          <span className="text-lg font-semibold text-muted-foreground">
                            /100
                          </span>
                        </p>
                        <p
                          className={`text-xs font-bold uppercase tracking-[0.15em] mt-1 ${riskLevelColor(
                            result.risk_assessment.level,
                          )}`}
                        >
                          {result.risk_assessment.level} RISK
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Flags */}
                {result.risk_assessment.flags.length > 0 && (
                  <div className="px-6 py-4 border-t border-white/[0.06]">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mb-3">
                      Risk flags
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {result.risk_assessment.flags.slice(0, 3).map((flag) => (
                        <Badge
                          key={flag}
                          variant="outline"
                          className="border-amber-500/30 text-amber-400 bg-amber-500/5 text-xs"
                        >
                          <AlertTriangle className="h-3 w-3 mr-1 shrink-0" />
                          {FLAG_LABELS[flag] ?? flag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* How we score · only shown when there's actually a score
                    to explain. Insufficient-data results skip this block. */}
                {result.risk_assessment.score != null && (
                  <div className="px-6 py-4 border-t border-white/[0.06]">
                    <ScoreExplainer
                      score={result.risk_assessment.score}
                      band={
                        result.kind === "broker" && result.country === "CA"
                          ? result.broker.verification_band ?? null
                          : null
                      }
                    />
                  </div>
                )}

                {/* Gated CTA */}
                <div className="px-6 py-5 border-t border-primary/10 bg-primary/[0.03]">
                  <Link
                    to="/signup?role=dispatcher&vertical=freight"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group"
                    onClick={() =>
                      track("try_it_now_upgrade_clicked", {
                        mode: result.kind,
                        recommendation: result.risk_assessment.recommendation,
                      })
                    }
                  >
                    {result.kind === "broker"
                      ? "Sign up to see bond history, claims, payment-on-time score, and full broker reputation"
                      : "Sign up to see full trust score history, insurance live-verify, driver-level details, and owner relationships"}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
